import { useState, useRef } from "react";
import { useAuth } from "../lib/auth.jsx";

/**
 * AvatarUpload - Profile picture upload component
 *
 * Features:
 * - File input with image preview
 * - Converts to base64 data URL
 * - Uploads to /api/account/profile/avatar or admin endpoint
 * - Shows loading and error states
 * - Accepts onSuccess callback for updating parent state
 */
export function AvatarUpload({
  currentAvatar = null,
  userId = null,
  onSuccess = () => {},
  size = 80
}) {
  const { authedFetch } = useAuth();
  const [preview, setPreview] = useState(currentAvatar);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  async function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be smaller than 5MB");
      return;
    }

    setError("");

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target.result; // data:image/png;base64,...
      setPreview(base64);

      // Upload to server
      setUploading(true);
      try {
        let response;
        if (userId) {
          // Admin updating another user's avatar
          response = await authedFetch(`/api/admin/technicians/${userId}`, {
            method: "PATCH",
            body: JSON.stringify({ avatar_url: base64 })
          });
        } else {
          // User updating their own avatar
          response = await authedFetch("/api/account/profile/avatar", {
            method: "POST",
            body: JSON.stringify({ avatar: base64 })
          });
        }

        const avatarUrl = response.avatar_url || response.technician?.avatar_url;
        setPreview(avatarUrl); // Use the permanent URL from storage
        onSuccess(avatarUrl);
      } catch (err) {
        setError(err.message || "Upload failed");
        setPreview(currentAvatar); // Revert to original on error
      } finally {
        setUploading(false);
      }
    };

    reader.readAsDataURL(file);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
      <div style={{ position: "relative" }}>
        {preview ? (
          <img
            src={preview}
            alt="Avatar preview"
            style={{
              width: size,
              height: size,
              borderRadius: "50%",
              objectFit: "cover",
              border: "3px solid var(--border-strong)",
              background: "var(--bg-soft)"
            }}
          />
        ) : (
          <div style={{
            width: size,
            height: size,
            borderRadius: "50%",
            background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontWeight: 700,
            fontSize: size * 0.4,
            border: "3px solid var(--border-strong)"
          }}>
            ?
          </div>
        )}

        {uploading && (
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 12,
            fontWeight: 600
          }}>
            <div style={{
              animation: "spin 1s linear infinite",
              fontSize: 20
            }}>⏳</div>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        disabled={uploading}
        style={{ display: "none" }}
      />

      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        style={{
          padding: "8px 16px",
          borderRadius: 8,
          fontWeight: 600,
          fontSize: 13,
          background: uploading ? "var(--bg-soft)" : "var(--bg-elevated)",
          color: uploading ? "var(--text-muted)" : "var(--text)",
          border: "1px solid var(--border-strong)",
          cursor: uploading ? "not-allowed" : "pointer",
          transition: "all 0.15s ease"
        }}
        onMouseEnter={(e) => {
          if (!uploading) e.currentTarget.style.borderColor = "var(--mauve)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "var(--border-strong)";
        }}
      >
        {uploading ? "Uploading..." : preview ? "Change Photo" : "Upload Photo"}
      </button>

      {error && (
        <div style={{
          fontSize: 12,
          color: "#C02020",
          textAlign: "center",
          maxWidth: 200
        }}>
          {error}
        </div>
      )}
    </div>
  );
}
